/**
 * ISO 3166-1 alpha-2 countries with English / Arabic names and nationalities. One dataset for the API, the web app
 * and the mock: packages/api-schema/reference/countries.json (GET /reference/countries serves the same file). The
 * bundled copy lets labels render without waiting for the request; the query in shared/lookups keeps it in step.
 */
import dataset from '@dealdesk/api-schema/reference/countries.json' with { type: 'json' };
import type { Schema } from '@dealdesk/api-schema';

export type Country = Schema<'Country'>;

export const COUNTRIES: readonly Country[] = dataset.countries;

const BY_CODE = new Map(COUNTRIES.map((c) => [c.code, c]));

export const isCountryCode = (v: unknown): v is string => typeof v === 'string' && BY_CODE.has(v);

export function countryByCode(code: string | null | undefined, list: readonly Country[] = COUNTRIES): Country | undefined {
  if (!code) return undefined;
  return list === COUNTRIES ? BY_CODE.get(code) : list.find((c) => c.code === code);
}

/** Lower case, accents removed, punctuation to spaces; Arabic letter variants unified (أ إ آ → ا, ى → ي, ة → ه). */
export function normalizeCountryText(v: string): string {
  return v
    .normalize('NFKD')
    // Accents, Arabic diacritics and the hamza / madda marks NFKD splits off (أ إ آ → ا), then tatweel.
    .replace(/\p{M}/gu, '')
    .replace(/ـ/g, '')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/** Every searchable term of a country: code, names, nationalities and aliases (normalised). */
export function countryTerms(c: Country): string[] {
  return [c.code, c.name, c.nameAr, c.nationality, c.nationalityAr, ...(c.aliases ?? [])].map(normalizeCountryText);
}

/** Countries matching a typed query (prefix of any word of any term, or the code); empty query → all. */
export function searchCountries(q: string, list: readonly Country[] = COUNTRIES): Country[] {
  const n = normalizeCountryText(q);
  if (!n) return [...list];
  // A word matches by prefix; an Arabic word also without its article ("الإمارات" matches "إمارات").
  const word = (w: string) => w.startsWith(n) || (w.startsWith('ال') && w.slice(2).startsWith(n));
  return list.filter((c) => countryTerms(c).some((t) => t.startsWith(n) || t.split(' ').some(word)));
}

/**
 * The country a free-text value means: an ISO code, an English / Arabic country name, a nationality or an alias
 * ("UAE", "Emirati", "United Kingdom", "KSA"). Exact term match only — never a guess. Null when not recognised.
 */
export function countryOf(v: unknown, list: readonly Country[] = COUNTRIES): Country | null {
  if (typeof v !== 'string' || !v.trim()) return null;
  const raw = v.trim();
  if (/^[A-Za-z]{2}$/.test(raw)) return countryByCode(raw.toUpperCase(), list) ?? null;
  const n = normalizeCountryText(raw);
  return list.find((c) => countryTerms(c).includes(n)) ?? null;
}

/** The label to show for a stored value: by locale, as country or nationality; unknown (legacy) values shown raw. */
export function countryLabel(value: string | null | undefined, opts: { locale: string; as?: 'country' | 'nationality'; list?: readonly Country[] }): string {
  if (!value) return '';
  const c = countryByCode(value, opts.list);
  if (!c) return value;
  const ar = opts.locale.split('-')[0] === 'ar';
  if (opts.as === 'nationality') return ar ? c.nationalityAr : c.nationality;
  return ar ? c.nameAr : c.name;
}
